import {
  FILTER_CHIP_ORDER,
  FILTER_KEY_ORDER,
  FilterGroups,
  FilterKeys,
  FilterObject,
  Filters,
  PageName,
  PageType,
  PageTypeAndName,
  ResponseConfig,
  SelectedFilters,
} from './pageConfig/Types';
import { updateAppliedFilters } from '@utils/FilterUtils';

export const filtersNotToRenderMap: Record<PageName, FilterKeys[]> = {
  [PageName.PERFORMANCE_ANALYSIS]: [],
  [PageName.TRADING_ASSISTANT]: [],
  [PageName.CATEGORY_LEADERBOARD]: [],
  [PageName.RETAILER_OVERVIEW]: [],
  [PageName.OPPORTUNITY_REVIEW]: [],
  [PageName.RI_PRODUCT]: [],
  [PageName.RI_PRODUCT_COMPARE]: [],
  [PageName.RI_PRODUCT_TRENDS]: [],
  [PageName.EXPLORE]: [],
};

export const multiCountryProductGroupConfigs: Record<
  PageType,
  { isMultiCountry: boolean; isMultiProductGroup: boolean }
> = {
  [PageType.PERFORMANCE_ANALYSIS]: { isMultiCountry: true, isMultiProductGroup: false },
  [PageType.TRADING_ASSISTANT]: { isMultiCountry: false, isMultiProductGroup: true },
  [PageType.CATEGORY_LEADERBOARD]: { isMultiCountry: false, isMultiProductGroup: true },
  [PageType.RETAILER_OVERVIEW]: { isMultiCountry: false, isMultiProductGroup: true },
  [PageType.OPPORTUNITY_REVIEW]: { isMultiCountry: false, isMultiProductGroup: true },
  [PageType.RI_PRODUCT]: { isMultiCountry: false, isMultiProductGroup: false },
  [PageType.EXPLORE]: { isMultiCountry: true, isMultiProductGroup: true },
};

/**
 * Defines the pages to carry over for specific side menu filters.
 *
 * Elegant way to map to an array of enum, compared to call a switch case method.
 */
export class FilterCarryOverScope {
  static readonly ALL = new FilterCarryOverScope(
    'ALL',
    Object.keys(PageType)
      .filter((key) => typeof PageType[key as keyof typeof PageType] === 'number')
      .map((key) => PageType[key as keyof typeof PageType]),
  );
  static readonly RO_CL = new FilterCarryOverScope('RO_CL', [
    PageType.RETAILER_OVERVIEW,
    PageType.CATEGORY_LEADERBOARD,
    PageType.OPPORTUNITY_REVIEW,
    PageType.EXPLORE,
  ]);
  static readonly OR_PA_TA_PRO = new FilterCarryOverScope('OR_PA_TA_PRO', [
    PageType.OPPORTUNITY_REVIEW,
    PageType.PERFORMANCE_ANALYSIS,
    PageType.TRADING_ASSISTANT,
    PageType.RI_PRODUCT,
    PageType.EXPLORE,
  ]);
  static readonly OR_PA_TA = new FilterCarryOverScope('OR_PA_TA', [
    PageType.OPPORTUNITY_REVIEW,
    PageType.PERFORMANCE_ANALYSIS,
    PageType.TRADING_ASSISTANT,
    PageType.EXPLORE,
  ]);
  static readonly PA_only = new FilterCarryOverScope('PA_only', [PageType.PERFORMANCE_ANALYSIS]);
  static readonly PERF_ANALYZER = new FilterCarryOverScope('PERF_ANALYZER', [
    PageType.RETAILER_OVERVIEW,
    PageType.CATEGORY_LEADERBOARD,
    PageType.OPPORTUNITY_REVIEW,
    PageType.PERFORMANCE_ANALYSIS,
    PageType.TRADING_ASSISTANT,
    PageType.EXPLORE,
  ]);
  static readonly PRODUCT_ANALYZER = new FilterCarryOverScope('PRODUCT_ANALYZER', [PageType.RI_PRODUCT]);
  static readonly TRADING_ASSISTANT = new FilterCarryOverScope('TRADING_ASSISTANT', [PageType.TRADING_ASSISTANT]);
  static readonly NONE = new FilterCarryOverScope('NONE', []);
  static readonly EXPLORE = new FilterCarryOverScope('EXPLORE', [
    PageType.RETAILER_OVERVIEW,
    PageType.EXPLORE,
    PageType.OPPORTUNITY_REVIEW,
    PageType.TRADING_ASSISTANT,
  ]);
  // Currency filter dedicated carry-over scope for Explore & OR pages
  static readonly CURRENCY_FILTER_SCOPE_EXPLORE_OR = new FilterCarryOverScope('CURRENCY_FILTER_SCOPE_EXPLORE_OR', [
    PageType.RETAILER_OVERVIEW,
    PageType.EXPLORE,
    PageType.OPPORTUNITY_REVIEW,
    PageType.TRADING_ASSISTANT,
    PageType.RI_PRODUCT,
  ]);
  // Currency filter dedicated carry-over scope for RO page
  static readonly CURRENCY_FILTER_SCOPE_RO = new FilterCarryOverScope('CURRENCY_FILTER_SCOPE_RO', [
    PageType.RETAILER_OVERVIEW,
    PageType.EXPLORE,
    PageType.OPPORTUNITY_REVIEW,
  ]);
  // Currency filter dedicated carry-over scope for TA & Product pages
  static readonly CURRENCY_FILTER_SCOPE_TA_PRODUCT = new FilterCarryOverScope('CURRENCY_FILTER_SCOPE_TA_PRODUCT', [
    PageType.EXPLORE,
    PageType.OPPORTUNITY_REVIEW,
    PageType.TRADING_ASSISTANT,
    PageType.RI_PRODUCT,
  ]);

  // private to disallow creating other instances of this type
  private constructor(
    private readonly key: string,
    public readonly value: PageType[],
  ) {}

  getPageTypes() {
    return this.value;
  }

  toString() {
    return this.key;
  }
}

export interface FilterConfig {
  filterKey: FilterKeys;
  isMulti: boolean;
  carryOverScope: FilterCarryOverScope;
}

const createFilters = (filters): FilterConfig[] =>
  filters.map(
    ([filterKey, isMulti, carryOverScope]): FilterConfig => ({
      filterKey,
      isMulti,
      carryOverScope,
    }),
  );

// Define the item to be filtered out
const currencyFilter = [FilterKeys.CURRENCY, false, FilterCarryOverScope.PA_only];

const sharedFilters_RO_CL = [
  [FilterKeys.PRODUCT_GROUP, true, FilterCarryOverScope.RO_CL],
  [FilterKeys.COUNTRY, false, FilterCarryOverScope.RO_CL],
  [FilterKeys.ONLINE_OFFLINE, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.REGION, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.CITY, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.BRAND, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.CHANNEL, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.ADDFEAT, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.SEGFEAT, true, FilterCarryOverScope.PERF_ANALYZER],
];

const sharedFilters_OR_PA_TA = [
  [FilterKeys.PRODUCT_GROUP, false, FilterCarryOverScope.OR_PA_TA_PRO],
  [FilterKeys.COUNTRY, false, FilterCarryOverScope.OR_PA_TA_PRO],
  [FilterKeys.ONLINE_OFFLINE, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.CHANNEL, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.REGION, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.CITY, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.BRAND, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.PRICEBAND, true, FilterCarryOverScope.OR_PA_TA],
  [FilterKeys.ADDFEAT, true, FilterCarryOverScope.PERF_ANALYZER],
  [FilterKeys.SEGFEAT, true, FilterCarryOverScope.PERF_ANALYZER],
];

const productFilters = createFilters([
  [FilterKeys.PRODUCT_GROUP, false, FilterCarryOverScope.OR_PA_TA_PRO],
  [FilterKeys.COUNTRY, false, FilterCarryOverScope.OR_PA_TA_PRO],
  [FilterKeys.ONLINE_OFFLINE, true, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.CHANNEL, true, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.REGION, true, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.CITY, true, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.BRAND, true, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.ADDFEAT, true, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.SEGFEAT, true, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.FIRST_ACTIVITY_START_DATE, false, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.FIRST_ACTIVITY_END_DATE, false, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.AVERAGE_PRICE_MIN, false, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.AVERAGE_PRICE_MAX, false, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.MODEL, false, FilterCarryOverScope.PRODUCT_ANALYZER],
  [FilterKeys.CURRENCY, false, FilterCarryOverScope.CURRENCY_FILTER_SCOPE_TA_PRODUCT],
]);

const retailerOverviewFilters = createFilters([
  ...sharedFilters_RO_CL,
  [FilterKeys.CURRENCY, false, FilterCarryOverScope.CURRENCY_FILTER_SCOPE_RO],
]);

const categoryLeaderboardFilters = createFilters([...sharedFilters_RO_CL]);
const opportunityReviewFilters = createFilters([
  [FilterKeys.PRODUCT_GROUP, true, FilterCarryOverScope.EXPLORE],
  [FilterKeys.COUNTRY, false, FilterCarryOverScope.EXPLORE],
  [FilterKeys.CURRENCY, false, FilterCarryOverScope.CURRENCY_FILTER_SCOPE_EXPLORE_OR],
  ...sharedFilters_OR_PA_TA,
]);

const performanceAnalysisFilters = createFilters([...sharedFilters_OR_PA_TA, currencyFilter]);

const tradingAssistantFilters = createFilters([
  [FilterKeys.PRODUCT_GROUP, true, FilterCarryOverScope.EXPLORE],
  [FilterKeys.COUNTRY, false, FilterCarryOverScope.EXPLORE],
  [FilterKeys.CURRENCY, false, FilterCarryOverScope.CURRENCY_FILTER_SCOPE_EXPLORE_OR],
  ...sharedFilters_OR_PA_TA,
]);

const exploreFilters = createFilters([
  [FilterKeys.PRODUCT_GROUP, true, FilterCarryOverScope.EXPLORE],
  [FilterKeys.COUNTRY, false, FilterCarryOverScope.EXPLORE],
  [FilterKeys.CURRENCY, false, FilterCarryOverScope.CURRENCY_FILTER_SCOPE_EXPLORE_OR],
  ...sharedFilters_OR_PA_TA,
]);

/*
 * settings for all the filters. mainly to check if it is single/multi selection type
 * more settings can be added on later if needed
 */

const filterSettings: Record<PageType, FilterConfig[]> = {
  [PageType.RETAILER_OVERVIEW]: retailerOverviewFilters,
  [PageType.CATEGORY_LEADERBOARD]: categoryLeaderboardFilters,
  [PageType.OPPORTUNITY_REVIEW]: opportunityReviewFilters,
  [PageType.PERFORMANCE_ANALYSIS]: performanceAnalysisFilters,
  [PageType.RI_PRODUCT]: productFilters,
  [PageType.TRADING_ASSISTANT]: tradingAssistantFilters,
  [PageType.EXPLORE]: exploreFilters,
};

export const getFilterSettingsByPage = (pageType: PageType) => {
  return filterSettings[pageType];
};

const baseFilters: FilterObject = {
  mainTitle: 'Base',
  filterGroup: FilterGroups.BASE,
  hasSubGroup: false,
  hasNormalAndSubGroup: false,
  subGroupFilters: [],
  filters: [],
};

const marketDefFilters: FilterObject = {
  mainTitle: 'Market Definition',
  filterGroup: FilterGroups.MARKET_DEFINITION,
  hasSubGroup: false,
  hasNormalAndSubGroup: false,
  subGroupFilters: [],
  filters: [],
};

const prodSpecFilters: FilterObject = {
  mainTitle: 'Product Specification',
  filterGroup: FilterGroups.PRODUCT_SPECIFICATION,
  hasSubGroup: true,
  hasNormalAndSubGroup: true,
  subGroupFilters: [
    {
      subGroupTitle: 'Product Features',
      filters: [],
    },
  ],
  filters: [],
};

const prodSpecProductFilters: FilterObject = {
  mainTitle: 'Product Specification',
  filterGroup: FilterGroups.PRODUCT_SPECIFICATION,
  hasSubGroup: false,
  hasNormalAndSubGroup: false,
  subGroupFilters: [],
  filters: [],
};

const prodPageSpecProductFilters: FilterObject = {
  mainTitle: 'Product Specification',
  filterGroup: FilterGroups.PRODUCT_SPECIFICATION,
  hasSubGroup: true,
  hasNormalAndSubGroup: false,
  subGroupFilters: [
    {
      subGroupTitle: 'First Activity',
      filters: [],
    },
    {
      subGroupTitle: 'Average Price',
      filters: [],
    },
  ],
  filters: [],
};

const prodFeaturesProductFilters: FilterObject = {
  mainTitle: '',
  filterGroup: FilterGroups.PRODUCT_SPECIFICATION,
  hasSubGroup: true,
  hasNormalAndSubGroup: false,
  subGroupFilters: [
    {
      subGroupTitle: 'Product Features',
      filters: [],
    },
  ],
  filters: [],
};

export const retailerOverviewResponseConfig: ResponseConfig = {
  pageType: PageType.RETAILER_OVERVIEW,
  response: [baseFilters, marketDefFilters, prodSpecFilters],
};

export const categoryLeaderboardResponseConfig: ResponseConfig = {
  pageType: PageType.CATEGORY_LEADERBOARD,
  response: [baseFilters, marketDefFilters, prodSpecProductFilters],
};

export const performanceAnalysisResponseConfig: ResponseConfig = {
  pageType: PageType.PERFORMANCE_ANALYSIS,
  response: [baseFilters, marketDefFilters, prodSpecFilters],
};

export const opportunityReviewResponseConfig: ResponseConfig = {
  pageType: PageType.OPPORTUNITY_REVIEW,
  response: [baseFilters, marketDefFilters, prodSpecFilters],
};

export const tradingAssistantResponseConfig: ResponseConfig = {
  pageType: PageType.TRADING_ASSISTANT,
  response: [baseFilters, marketDefFilters, prodSpecFilters],
};

export const productsResponseConfig: ResponseConfig = {
  pageType: PageType.RI_PRODUCT,
  response: [baseFilters, marketDefFilters, prodPageSpecProductFilters, prodFeaturesProductFilters],
};

export const exploreResponseConfig: ResponseConfig = {
  pageType: PageType.EXPLORE,
  response: [baseFilters, marketDefFilters, prodSpecFilters],
};

const pageFilterResponseConfig: Record<PageType, ResponseConfig> = {
  [PageType.RETAILER_OVERVIEW]: retailerOverviewResponseConfig,
  [PageType.CATEGORY_LEADERBOARD]: categoryLeaderboardResponseConfig,
  [PageType.OPPORTUNITY_REVIEW]: opportunityReviewResponseConfig,
  [PageType.PERFORMANCE_ANALYSIS]: performanceAnalysisResponseConfig,
  [PageType.RI_PRODUCT]: productsResponseConfig,
  [PageType.TRADING_ASSISTANT]: tradingAssistantResponseConfig,
  [PageType.EXPLORE]: exploreResponseConfig,
};

export const getFilterResponseConfigByPageType = (pageType: PageType): ResponseConfig => {
  return pageFilterResponseConfig[pageType];
};

const currencyBaseFilter: FilterKeys[] = [FilterKeys.CURRENCY];
const countryAndCurrencyBaseFilter: FilterKeys[] = [FilterKeys.COUNTRY, FilterKeys.CURRENCY];
const pgAndCurrencyBaseFilter: FilterKeys[] = [FilterKeys.PRODUCT_GROUP, FilterKeys.CURRENCY];
const countryAndCurrencyAndBrandBaseFilter: FilterKeys[] = [FilterKeys.BRAND, FilterKeys.COUNTRY, FilterKeys.CURRENCY];
const pgAndCountryAndCurrencyBaseFilter: FilterKeys[] = [
  FilterKeys.PRODUCT_GROUP,
  FilterKeys.COUNTRY,
  FilterKeys.CURRENCY,
];

// get the base filter keys by page type and page name
// pageName is passed to allow clear filter not clearing brand for TA
export const getBaseFilterKeysByPageTypeAndPageName = (
  pageInfo: PageTypeAndName,
  filters: Filters[],
  isMultiCountryClient?: boolean,
): FilterKeys[] => {
  switch (pageInfo.pageType) {
    case PageType.PERFORMANCE_ANALYSIS:
      if (isMultiCountryClient) {
        return pgAndCurrencyBaseFilter;
      }
      return pgAndCountryAndCurrencyBaseFilter;
    case PageType.RI_PRODUCT:
      return pgAndCountryAndCurrencyBaseFilter;
    case PageType.EXPLORE:
      if (isMultiCountryClient) {
        return currencyBaseFilter;
      }
      return countryAndCurrencyBaseFilter;
    case PageType.TRADING_ASSISTANT:
      return getBaseFilterKeysForTradingAssistantPage(filters);
    case PageType.RETAILER_OVERVIEW:
    case PageType.OPPORTUNITY_REVIEW:
    case PageType.CATEGORY_LEADERBOARD:
    default:
      return countryAndCurrencyBaseFilter;
  }
};

/**
 * Verifies and get the base filter keys for Trading Assistant page based on selected filters.
 * There are two possibilities base filter keys:
 * 1 - COUNTRY, CURRENCY and BRAND (If selected one or more brand on TA page).
 * 2 - COUNTRY and CURRENCY (Without select any one of brand on TA page).
 * @param filters
 */
const getBaseFilterKeysForTradingAssistantPage = (filters: Filters[]): FilterKeys[] => {
  if (!Array.isArray(filters) || filters.length === 0) {
    return countryAndCurrencyBaseFilter; // Return default base filter keys without brand selection.
  }
  const isBrandSelected: boolean = filters.some((element) => {
    return element.filterKey === FilterKeys.BRAND;
  });
  return isBrandSelected ? countryAndCurrencyAndBrandBaseFilter : countryAndCurrencyBaseFilter;
};

/*
 * function to keep the base filters in pg overview from being cleared
 */
export const keepBaseFilters = (
  allFilters: SelectedFilters[],
  pageTypeAndName: PageTypeAndName,
  updateContent: SelectedFilters,
  isMultiCountry?: boolean,
  isKeepMultiSelectCountries?: boolean,
): SelectedFilters => {
  const selectedFilters = allFilters[pageTypeAndName.pageType].selectedFilters;
  const base: Filters[] = [];

  const isPaPage = pageTypeAndName.pageName === PageName.PERFORMANCE_ANALYSIS;

  if (isPaPage && isMultiCountry) {
    keepMultiCountryBaseFilters(pageTypeAndName, selectedFilters, base);
  } else if (
    pageTypeAndName.pageType === PageType.EXPLORE ||
    pageTypeAndName.pageType === PageType.OPPORTUNITY_REVIEW ||
    pageTypeAndName.pageType === PageType.TRADING_ASSISTANT
  ) {
    keepExploreBaseFilters(pageTypeAndName, selectedFilters, base, isKeepMultiSelectCountries);
  } else {
    getBaseFilterKeysByPageTypeAndPageName(pageTypeAndName, selectedFilters).forEach((key) => {
      const index = selectedFilters.findIndex((e) => e.filterKey === key);
      //To ensure empty brand filter wont get pushed as -1 causing the element break
      if (index >= 0) {
        base.push(selectedFilters[index]);
      }
    });
  }
  updateContent.selectedFilters = base;

  return updateContent;
};

export const keepExploreBaseFilters = (
  pageTypeAndName: PageTypeAndName,
  selectedFilters: Filters[],
  multiCountryBaseFilters: Filters[],
  isKeepMultiSelectCountries?: boolean,
): Filters[] => {
  const initialBaseFilters = [...getBaseFilterKeysByPageTypeAndPageName(pageTypeAndName, selectedFilters)];
  initialBaseFilters.push(FilterKeys.PRODUCT_GROUP);
  initialBaseFilters.forEach((x) => {
    const index = selectedFilters.findIndex((e) => e.filterKey === x);

    if (index >= 0) {
      // This function is used by both filter chips and side filters.
      // For filter chips, it clears all selected countries except the base country.
      // For side filters, it retains selected countries when a new product group (pg) is selected without applying it.
      if (selectedFilters[index].filterKey === FilterKeys.COUNTRY && !isKeepMultiSelectCountries) {
        selectedFilters[index].options.splice(1, selectedFilters[index].options.length - 1);
      }

      updateAppliedFilters(selectedFilters[index], multiCountryBaseFilters);
    }
  });
  return multiCountryBaseFilters;
};

/* Function to retain the base filters on PA page when multi country client is active.
 * It retains all the selected pg filters but splices the country filter
 * to only retain the first selected country
 *
 * @param pageTypeAndName the currrent page type and name
 * @param selectedFilters  the current selected filters
 * @param base the base filters to be retained
 */

export const keepMultiCountryBaseFilters = (
  pageTypeAndName: PageTypeAndName,
  selectedFilters: Filters[],
  multiCountryBaseFilters: Filters[],
): Filters[] => {
  getBaseFilterKeysByPageTypeAndPageName(pageTypeAndName, selectedFilters).forEach((x) => {
    const index = selectedFilters.findIndex((e) => e.filterKey === x);

    if (index >= 0) {
      if (selectedFilters[index].filterKey === FilterKeys.COUNTRY) {
        selectedFilters[index].options.splice(1, selectedFilters[index].options.length - 1);
      }
      multiCountryBaseFilters.push(selectedFilters[index]);
    }
  });
  return multiCountryBaseFilters;
};

/*
 * function that clears all the filters. it will keep the base filters if pageType === overview page
 */
export const clearAllFilters = (
  filters: SelectedFilters[],
  pageTypeAndName: PageTypeAndName,
  isMultiCountry?: boolean,
  isKeepMultiSelectCountries?: boolean,
): SelectedFilters[] => {
  const allFilters = [...filters];
  const { pageType } = pageTypeAndName;
  let updateContent: SelectedFilters = { pageType, selectedFilters: [] };
  updateContent = keepBaseFilters(
    allFilters,
    pageTypeAndName,
    updateContent,
    isMultiCountry,
    isKeepMultiSelectCountries,
  );

  allFilters[pageType] = updateContent;
  return allFilters;
};

/*
 * function that sorts the options from the api response alphabetically
 */
export const sortOptionsFromApi = (options: { value: string; label: string }[]): { value: string; label: string }[] => {
  options = [...options].sort((a, b) => (a.label > b.label ? 1 : -1));

  return options;
};

/*
 * function that sorts by filterTitle alphabetically
 */
export const sortFilterTitle = (filters: Filters[]) => {
  filters = [...filters].sort((a, b) => (a.filterTitle > b.filterTitle ? 1 : -1));
  return filters;
};

/*
 * function that sorts the filters in order of the filter key
 */
export const sortFilters = (filters: Filters[]): Filters[] => {
  return [...filters].sort((a, b) => FILTER_KEY_ORDER[a.filterKey] - FILTER_KEY_ORDER[b.filterKey]);
};

/*
 * function that sorts the filterChips in order of the filter key
 */
export const sortFiltersChips = (filters: Filters[]): Filters[] => {
  return [...filters].sort((a, b) => FILTER_CHIP_ORDER[a.filterKey] - FILTER_CHIP_ORDER[b.filterKey]);
};

/*
 * function that sorts the priceband options in order
 */
export const sortPriceBandOptions = (
  options: { value: string; label: string }[],
): { value: string; label: string }[] => {
  if (options.length !== 0) {
    const firstOption = [...options].filter((x) => x.label.trim().startsWith('<=')); // changed to startsWith
    const lastOption = [...options].filter((x) => (x.label.match(/=/g) || []).length === 0);

    let remainingOptions = [...options].filter((x) => x.label !== firstOption[0].label);
    remainingOptions = [...remainingOptions].filter((x) => x.label !== lastOption[0].label);

    options = [...remainingOptions].sort(
      (a, b) =>
        parseFloat(a.label.split('<')[0].substring(1).replace(',', '')) -
        parseFloat(b.label.split('<')[0].substring(1).replace(',', '')),
    );
    options = firstOption.concat(options);
    options = options.concat(lastOption);
  }

  return options;
};

/**
 * Returns an array of `FilterObject` to render within the side menu for a given page.
 *
 * Filters that should not be rendered for the given page are excluded.
 *
 * @param pageName - The current page name.
 * @param filterObjects - The current list of filters to render in the side menu filter.
 */
export const filtersToRender = (pageName: PageName, filterObjects: FilterObject[]): FilterObject[] => {
  // Safe to assume that it will always be found (i.e. not null) since each ResponseConfig object
  // is always initialized with a default base filter group
  const baseFilterGroup = filterObjects.find((filterObject) => filterObject.filterGroup === FilterGroups.BASE)!;

  const filtersNotToRender = filtersNotToRenderMap[pageName];

  baseFilterGroup.filters = baseFilterGroup.filters.filter((filter) => !filtersNotToRender.includes(filter.filterKey));

  return filterObjects;
};
